<template>
  <form @submit.prevent="driverProfileUpdate">
    <div class="shadow sm:rounded-md sm:overflow-hidden">
      <div class="bg-white py-6 px-4 space-y-6 sm:p-6">
        <div>
          <h3 class="text-lg leading-6 font-medium text-gray-900">
            Skills and Work Information
          </h3>
          <p class="mt-1 text-sm text-gray-500">
            This information will be used to show you relevant job search
            results.
          </p>
        </div>

        <div class="grid grid-cols-6 gap-6">
          <div class="col-span-6">
            <SwitchGroup as="div" class="flex items-center justify-between">
              <span class="flex-grow flex flex-col">
                <SwitchLabel
                  as="span"
                  class="text-sm font-medium text-gray-900"
                  passive
                >
                  Work eligibility
                </SwitchLabel>
                <SwitchDescription as="span" class="text-sm text-gray-500">
                  Are you eligible to work in the USA?
                </SwitchDescription>
              </span>
              <Switch
                v-model="form.work_in_usa"
                :class="[
                  form.work_in_usa ? 'bg-blue-600' : 'bg-gray-200',
                  'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500',
                ]"
              >
                <span
                  aria-hidden="true"
                  :class="[
                    form.work_in_usa ? 'translate-x-5' : 'translate-x-0',
                    'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200',
                  ]"
                />
              </Switch>
            </SwitchGroup>
          </div>

          <div class="col-span-6">
            <SwitchGroup as="div" class="flex items-center justify-between">
              <span class="flex-grow flex flex-col">
                <SwitchLabel
                  as="span"
                  class="text-sm font-medium text-gray-900"
                  passive
                >
                  English skills
                </SwitchLabel>
                <SwitchDescription as="span" class="text-sm text-gray-500">
                  Can you speak and write English fluently?
                </SwitchDescription>
              </span>
              <Switch
                v-model="form.english_fluent"
                :class="[
                  form.english_fluent ? 'bg-blue-600' : 'bg-gray-200',
                  'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500',
                ]"
              >
                <span
                  aria-hidden="true"
                  :class="[
                    form.english_fluent ? 'translate-x-5' : 'translate-x-0',
                    'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200',
                  ]"
                />
              </Switch>
            </SwitchGroup>
          </div>

          <div class="col-span-6">
            <SwitchGroup as="div" class="flex items-center justify-between">
              <span class="flex-grow flex flex-col">
                <SwitchLabel
                  as="span"
                  class="text-sm font-medium text-gray-900"
                  passive
                >
                  Employment Status
                </SwitchLabel>
                <SwitchDescription as="span" class="text-sm text-gray-500">
                  Are you currently employed?
                </SwitchDescription>
              </span>
              <Switch
                v-model="form.currently_employed"
                :class="[
                  form.currently_employed ? 'bg-blue-600' : 'bg-gray-200',
                  'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500',
                ]"
              >
                <span
                  aria-hidden="true"
                  :class="[
                    form.currently_employed ? 'translate-x-5' : 'translate-x-0',
                    'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200',
                  ]"
                />
              </Switch>
            </SwitchGroup>
          </div>

          <div
            v-show="!form.currently_employed"
            class="col-span-6 md:col-span-4 lg:col-span-3"
          >
            <label
              for="last_employed"
              class="block text-sm font-medium text-gray-700"
              >Last Employed</label
            >
            <div class="mt-1">
              <input
                v-model="form.last_employed"
                type="date"
                name="last_employed"
                id="last_employed"
                class="
                  shadow-sm
                  focus:ring-blue-500 focus:border-blue-500
                  block
                  w-full
                  sm:text-sm
                  border-gray-300
                  rounded-md
                "
                placeholder="you@example.com"
              />
            </div>
          </div>

          <div class="border-t col-span-6"></div>

          <div class="col-span-6 md:col-span-3">
            <div>
              <label
                for="cdl_class"
                class="block text-sm font-medium text-gray-700"
                >CDL Class</label
              >
              <select
                @change="form.cdl_class = $event.target.value"
                id="cdl_class"
                name="cdl_class"
                class="
                  mt-1
                  block
                  w-full
                  pl-3
                  pr-10
                  py-2
                  text-base
                  border-gray-300
                  focus:outline-none focus:ring-blue-500 focus:border-blue-500
                  sm:text-sm
                  rounded-md
                "
              >
                <option :selected="!form.cdl_class"></option>
                <option value="a" :selected="form.cdl_class === 'a'">
                  CDL A
                </option>
                <option value="b" :selected="form.cdl_class === 'b'">
                  CDL B
                </option>
              </select>
            </div>
          </div>

          <div class="col-span-6 md:col-span-3">
            <div>
              <label
                for="cdl_state"
                class="block text-sm font-medium text-gray-700"
                >CDL State</label
              >
              <select
                @change="form.cdl_state = $event.target.value"
                id="cdl_state"
                name="cdl_state"
                class="
                  mt-1
                  block
                  w-full
                  pl-3
                  pr-10
                  py-2
                  text-base
                  border-gray-300
                  focus:outline-none focus:ring-blue-500 focus:border-blue-500
                  sm:text-sm
                  rounded-md
                "
              >
                <option :selected="!form.cdl_state"></option>
                <option
                  v-for="state in states"
                  :key="`state-${state[0]}`"
                  :value="state[0]"
                  :selected="state[0] === form.cdl_state"
                >
                  {{ state[1] }}
                </option>
              </select>
            </div>
          </div>

          <div class="col-span-6">
            <div>
              <label
                for="drug_test"
                class="block text-sm font-medium text-gray-700"
                >Pre-employment Drug Test</label
              >
              <select
                @change="form.drug_test = $event.target.value"
                id="drug_test"
                name="drug_test"
                class="
                  mt-1
                  block
                  w-full
                  pl-3
                  pr-10
                  py-2
                  text-base
                  border-gray-300
                  focus:outline-none focus:ring-blue-500 focus:border-blue-500
                  sm:text-sm
                  rounded-md
                "
              >
                <option value="both" :selected="form.drug_test === 'both'">
                  I can pass both hair and urine drug tests
                </option>
                <option value="hair" :selected="form.drug_test === 'hair'">
                  I can pass a hair test
                </option>
                <option value="urine" :selected="form.drug_test === 'urine'">
                  I can pass a urine test
                </option>
              </select>
            </div>
          </div>

          <div class="border-t col-span-6"></div>

          <div class="col-span-6">
            <SwitchGroup as="div" class="flex items-center justify-between">
              <span class="flex-grow flex flex-col">
                <SwitchLabel
                  as="span"
                  class="text-sm font-medium text-gray-900"
                  passive
                >
                  Touch Freight
                </SwitchLabel>
                <SwitchDescription as="span" class="text-sm text-gray-500">
                  Are you willing to touch freight?
                </SwitchDescription>
              </span>
              <Switch
                v-model="form.touch_freight"
                :class="[
                  form.touch_freight ? 'bg-blue-600' : 'bg-gray-200',
                  'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500',
                ]"
              >
                <span
                  aria-hidden="true"
                  :class="[
                    form.touch_freight ? 'translate-x-5' : 'translate-x-0',
                    'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200',
                  ]"
                />
              </Switch>
            </SwitchGroup>
          </div>

          <div class="col-span-6">
            <SwitchGroup as="div" class="flex items-center justify-between">
              <span class="flex-grow flex flex-col">
                <SwitchLabel
                  as="span"
                  class="text-sm font-medium text-gray-900"
                  passive
                >
                  TWIC
                </SwitchLabel>
                <SwitchDescription as="span" class="text-sm text-gray-500">
                  Transportation Worker Identification Credential
                </SwitchDescription>
              </span>
              <Switch
                v-model="form.twic"
                :class="[
                  form.twic ? 'bg-blue-600' : 'bg-gray-200',
                  'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500',
                ]"
              >
                <span
                  aria-hidden="true"
                  :class="[
                    form.twic ? 'translate-x-5' : 'translate-x-0',
                    'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200',
                  ]"
                />
              </Switch>
            </SwitchGroup>
          </div>

          <div class="col-span-6">
            <SwitchGroup as="div" class="flex items-center justify-between">
              <span class="flex-grow flex flex-col">
                <SwitchLabel
                  as="span"
                  class="text-sm font-medium text-gray-900"
                  passive
                >
                  SAP
                </SwitchLabel>
                <SwitchDescription as="span" class="text-sm text-gray-500">
                  Are you currently in an SAP program?
                </SwitchDescription>
              </span>
              <Switch
                v-model="form.sap"
                :class="[
                  form.sap ? 'bg-blue-600' : 'bg-gray-200',
                  'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500',
                ]"
              >
                <span
                  aria-hidden="true"
                  :class="[
                    form.sap ? 'translate-x-5' : 'translate-x-0',
                    'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200',
                  ]"
                />
              </Switch>
            </SwitchGroup>
          </div>

          <div class="col-span-6">
            <SwitchGroup as="div" class="flex items-center justify-between">
              <span class="flex-grow flex flex-col">
                <SwitchLabel
                  as="span"
                  class="text-sm font-medium text-gray-900"
                  passive
                >
                  Pet
                </SwitchLabel>
                <SwitchDescription as="span" class="text-sm text-gray-500">
                  Do you have a pet that rides with you?
                </SwitchDescription>
              </span>
              <Switch
                v-model="form.pet"
                :class="[
                  form.pet ? 'bg-blue-600' : 'bg-gray-200',
                  'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500',
                ]"
              >
                <span
                  aria-hidden="true"
                  :class="[
                    form.pet ? 'translate-x-5' : 'translate-x-0',
                    'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200',
                  ]"
                />
              </Switch>
            </SwitchGroup>
          </div>

          <div class="col-span-6">
            <SwitchGroup as="div" class="flex items-center justify-between">
              <span class="flex-grow flex flex-col">
                <SwitchLabel
                  as="span"
                  class="text-sm font-medium text-gray-900"
                  passive
                >
                  Rider
                </SwitchLabel>
                <SwitchDescription as="span" class="text-sm text-gray-500">
                  Do you have a rider that rides with you?
                </SwitchDescription>
              </span>
              <Switch
                v-model="form.rider"
                :class="[
                  form.rider ? 'bg-blue-600' : 'bg-gray-200',
                  'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500',
                ]"
              >
                <span
                  aria-hidden="true"
                  :class="[
                    form.rider ? 'translate-x-5' : 'translate-x-0',
                    'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200',
                  ]"
                />
              </Switch>
            </SwitchGroup>
          </div>

          <div class="border-t col-span-6"></div>

          <div class="col-span-6">
            <h3 class="text-sm font-medium text-gray-900">Endorsements</h3>

            <fieldset class="mt-4 grid grid-cols-2 gap-5">
              <legend class="sr-only">Endorsements</legend>
              <div class="relative flex items-start">
                <div class="flex items-center h-5">
                  <input
                    id="hazmat"
                    type="checkbox"
                    v-model="form.endorsements.h"
                    class="
                      focus:ring-blue-500
                      h-4
                      w-4
                      text-blue-600
                      border-gray-300
                      rounded
                    "
                  />
                </div>
                <div class="ml-3 text-sm">
                  <label for="hazmat">(H) Hazmat</label>
                </div>
              </div>
              <div class="relative flex items-start">
                <div class="flex items-center h-5">
                  <input
                    id="tank"
                    v-model="form.endorsements.t"
                    type="checkbox"
                    class="
                      focus:ring-blue-500
                      h-4
                      w-4
                      text-blue-600
                      border-gray-300
                      rounded
                    "
                  />
                </div>
                <div class="ml-3 text-sm">
                  <label for="tank">(T) Tank Vehicle</label>
                </div>
              </div>
              <div class="relative flex items-start">
                <div class="flex items-center h-5">
                  <input
                    id="passenger_transport"
                    v-model="form.endorsements.p"
                    type="checkbox"
                    class="
                      focus:ring-blue-500
                      h-4
                      w-4
                      text-blue-600
                      border-gray-300
                      rounded
                    "
                  />
                </div>
                <div class="ml-3 text-sm">
                  <label for="passenger_transport"
                    >(P) Passenger Transport</label
                  >
                </div>
              </div>
              <div class="relative flex items-start">
                <div class="flex items-center h-5">
                  <input
                    id="school_bus"
                    v-model="form.endorsements.s"
                    type="checkbox"
                    class="
                      focus:ring-blue-500
                      h-4
                      w-4
                      text-blue-600
                      border-gray-300
                      rounded
                    "
                  />
                </div>
                <div class="ml-3 text-sm">
                  <label for="school_bus"
                    >(S) School Bus / Passenger Transport</label
                  >
                </div>
              </div>
              <div class="relative flex items-start">
                <div class="flex items-center h-5">
                  <input
                    id="doubles_triples"
                    v-model="form.endorsements.t"
                    type="checkbox"
                    class="
                      focus:ring-blue-500
                      h-4
                      w-4
                      text-blue-600
                      border-gray-300
                      rounded
                    "
                  />
                </div>
                <div class="ml-3 text-sm">
                  <label for="doubles_triples">(T) Doubles Tripples</label>
                </div>
              </div>
              <div class="relative flex items-start">
                <div class="flex items-center h-5">
                  <input
                    id="tanker_hazardous"
                    v-model="form.endorsements.x"
                    type="checkbox"
                    class="
                      focus:ring-blue-500
                      h-4
                      w-4
                      text-blue-600
                      border-gray-300
                      rounded
                    "
                  />
                </div>
                <div class="ml-3 text-sm">
                  <label for="tanker_hazardous"
                    >(X) Tanker and Hazardous Materials</label
                  >
                </div>
              </div>
            </fieldset>
          </div>

          <div class="border-t col-span-6"></div>

          <div class="col-span-6">
            <div>
              <label
                for="driving_experience"
                class="block text-sm font-medium text-gray-700"
                >How much driving experience do you have?</label
              >
              <select
                @change="form.driving_experience = $event.target.value"
                id="driving_experience"
                name="driving_experience"
                class="
                  mt-1
                  block
                  w-full
                  pl-3
                  pr-10
                  py-2
                  text-base
                  border-gray-300
                  focus:outline-none focus:ring-blue-500 focus:border-blue-500
                  sm:text-sm
                  rounded-md
                "
              >
                <option selected disabled class="text-gray-700">
                  please select your experience
                </option>
                <option value="10" :selected="form.driving_experience === 10">
                  I just graduated driving school, looking for my first job
                </option>
                <option value="11" :selected="form.driving_experience === 11">
                  3 - 6 months of CDL driving experience
                </option>
                <option value="12" :selected="form.driving_experience === 12">
                  6 - 12 months of CDL driving experience
                </option>
                <option value="13" :selected="form.driving_experience === 13">
                  12 - 24 months of CDL driving experience
                </option>
                <option value="14" :selected="form.driving_experience === 14">
                  24 - 48 months of CDL driving experience
                </option>
                <option value="15" :selected="form.driving_experience === 15">
                  over 4 years of CDL driving experience
                </option>
              </select>
            </div>

            <h3 class="mt-6 text-sm font-medium text-gray-900">Experiences</h3>
            <p class="text-sm text-gray-500">
              What type of CDL Driving experience do you have ?
            </p>

            <fieldset class="mt-4 grid grid-cols-2 gap-5">
              <legend class="sr-only">Experiences</legend>
              <div
                class="relative flex items-start"
                v-for="(experience, index) in experiences"
                :key="`experience-${index}`"
              >
                <div class="flex items-center h-5">
                  <input
                    :id="experience.key"
                    :name="experience.key"
                    v-model="form.experiences[experience.key]"
                    type="checkbox"
                    class="
                      focus:ring-blue-500
                      h-4
                      w-4
                      text-blue-600
                      border-gray-300
                      rounded
                    "
                  />
                </div>
                <div class="ml-3 text-sm">
                  <label :for="experience.key">{{ experience.label }}</label>
                </div>
              </div>
            </fieldset>
          </div>

          <div class="border-t col-span-6"></div>

          <div class="col-span-6">
            <h3 class="text-sm font-medium text-gray-900">
              Preferred Job Type
            </h3>
            <p class="text-sm text-gray-500">
              What kind of job are you looking for?
            </p>
            <fieldset class="space-y-5">
              <legend class="sr-only">Job Type</legend>
              <div
                class="relative flex items-start"
                v-for="(jobType, index) in jobTypes"
                :key="`jobType-${index}`"
              >
                <div class="flex items-center h-5">
                  <input
                    :id="jobType.key"
                    :name="jobType.key"
                    :value="jobType.key"
                    v-model="form.preferred_job_type[jobType.key]"
                    type="checkbox"
                    class="
                      focus:ring-blue-500
                      h-4
                      w-4
                      text-blue-600
                      border-gray-300
                      rounded
                    "
                  />
                </div>
                <div class="ml-3 text-sm">
                  <label :for="jobType.key">{{ jobType.label }}</label>
                </div>
              </div>
            </fieldset>
          </div>
          <div class="border-t col-span-6"></div>
          <div class="col-span-6">
            <h3 class="text-sm font-medium text-gray-900">
              Upload CV or Resume
            </h3>
            <p class="text-sm text-gray-500">Click below to upload</p>
            <small v-if="form.resume" class="flex items-center py-2 text-blue-400">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="w-5 h-5"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
                />
              </svg>
              <p class="px-2">you've already uploaded a resume before, if you want to change it you can
              upload a new one.</p>
              </small
            >

            <div class="flex items-center justify-center w-full py-2">
              <label
                for="dropzone-file"
                :class="[resumeName ? 'border-2 border-green-500' : '']"
                class="
                  flex flex-col
                  items-center
                  justify-center
                  w-full
                  h-64
                  border-2 border-gray-300 border-dashed
                  rounded-lg
                  cursor-pointer
                  bg-gray-50
                  dark:hover:bg-bray-800 dark:bg-gray-700
                  hover:bg-gray-100
                  dark:border-gray-600
                  dark:hover:border-gray-500
                  dark:hover:bg-gray-600
                "
              >
                <div
                  class="flex flex-col items-center justify-center pt-5 pb-6"
                >
                  <svg
                    v-if="resumeName"
                    aria-hidden="true"
                    class="w-10 h-10 mb-3 text-green-400"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>

                  <svg
                    v-else
                    aria-hidden="true"
                    class="w-10 h-10 mb-3 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    ></path>
                  </svg>
                  <p
                    v-if="!resumeName"
                    class="mb-2 text-sm text-gray-500 dark:text-gray-400"
                  >
                    <span class="font-semibold">Click to upload</span>
                  </p>
                  <p
                    v-if="!resumeName"
                    class="text-xs text-gray-500 dark:text-gray-400"
                  >
                    PDF, DOC or DOCX ( MAX. 1MB )
                  </p>
                  <p :class="[resumeName ? 'py-5' : 'hidden']">
                    {{ resumeName ?? "-" }}
                  </p>
                </div>
                <input
                  id="dropzone-file"
                  type="file"
                  class="hidden"
                  accept="application/pdf,application/msword,
                          application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  @change="onResumeChange"
                  @input="form.resume = $event.target.files[0]"
                />
              </label>
            </div>
            <div
              class="text-xs text-red-500 mt-1 tracking-wide"
              v-if="form.errors.resume"
              v-text="form.errors.resume"
            ></div>
          </div>
        </div>
      </div>
      <div class="px-4 py-3 bg-gray-50 text-right sm:px-6">
        <button
          type="submit"
          class="
            bg-blue-600
            border border-transparent
            rounded-md
            shadow-sm
            py-2
            px-4
            inline-flex
            justify-center
            text-sm
            font-medium
            text-white
            hover:bg-blue-700
            focus:outline-none
            focus:ring-2
            focus:ring-offset-2
            focus:ring-blue-500
          "
        >
          Save
        </button>
      </div>
    </div>
  </form>
</template>

<script>
const experiences = [
  { label: "Flat Bed Hauling", key: "flat_bed" },
  { label: "Heavy Equipment Hauling", key: "heavy_equipment" },
  { label: "Dry Van 53'", key: "dry_van_53" },
  { label: "Dry Van (less than 53')", key: "dry_van_less_53" },
  { label: "Refer 53'", key: "refer_53" },
  { label: "Refer (less than 53')", key: "refer_less_53" },
  { label: "Tanker Trailer", key: "tanker_trailer" },
  { label: "Tanker Truck", key: "tanker_truck" },
  { label: "Box Truck", key: "box_truck" },
  { label: "School Bus", key: "school_bus" },
  { label: "Tour Bus", key: "tour_bus" },
  { label: "Log Hauling", key: "log_hauling" },
  { label: "Livestock Hauling", key: "livestock_hauling" },
  { label: "Dump Truck", key: "dump_truck" },
  { label: "Sanitation Truck", key: "sanitation_truck" },
];

const jobTypes = [
  { label: "Local driving, I want to be home daily.", key: "local" },
  {
    label: "Regional driving, I want to be home at least once a week.",
    key: "regional",
  },
  {
    label: "Over The Road driving, I want to be home every other week.",
    key: "otr",
  },
  {
    label: "Extended Over The Road driving, I want to be out a month or more.",
    key: "eotr",
  },
  { label: "I am looking for Part Time / Casual Driving.", key: "casual" },
  { label: "I am looking for anything.", key: "any" },
];

import { useForm } from "@inertiajs/inertia-vue3";
import { ref } from "vue";

import {
  Switch,
  SwitchDescription,
  SwitchGroup,
  SwitchLabel,
} from "@headlessui/vue";
import states from "@/../assets/states.js";

export default {
  props: {
    profile: Object,
  },
  setup(props) {
    const { profile } = props;
    let resumeUrl = ref(null);
    let resumeName = ref(null);

    const expKeys = {};
    experiences.forEach((exp) => Object.assign(expKeys, { [exp.key]: false }));

    const form = useForm(
      profile || {
        work_in_usa: false,
        english_fluent: false,
        currently_employed: false,
        last_employed: "",
        cdl_class: "",
        cdl_state: "",
        drug_test: "",
        touch_freight: false,
        twic: false,
        sap: false,
        pet: false,
        rider: false,
        endorsements: {
          h: false,
          t: false,
          p: false,
          t: false,
          x: false,
        },
        driving_experience: "",
        experiences: expKeys,
        preferred_job_type: {
          local: false,
          causal: false,
          regional: false,
          otr: false,
          eotr: false,
          any: false,
        },
        resume: null,
      }
    );

    const route = window.route;

    const driverProfileUpdate = async () => {
      if (!resumeUrl.value) {
        delete form.resume;
      }
      form.post(route("profile.update"));
    };

    function onResumeChange(e) {
      const file = e.target.files[0];
      resumeUrl.value = URL.createObjectURL(file);
      resumeName.value = file.name;
    }

    return {
      form,
      states: Object.entries(states),
      jobTypes,
      experiences,
      resumeUrl,
      resumeName,
      onResumeChange,
      driverProfileUpdate,
    };
  },

  components: {
    Switch,
    SwitchDescription,
    SwitchGroup,
    SwitchLabel,
  },
};
</script>
